

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import axios from 'axios';

const SecurityDataContext = createContext();

const SecurityDataProvider = ({ children }) => {
  const [securityData, setSecurityData] = useState({
    s3Data: null,
    lbData: null,
    ec2Data: null,
    rdsData: null,
    ecsData: null,
    kmsData: null,
    loginData: null,
    wafData:null
  });
  const [filterState, setFilterState] = useState({
    region: 'us-east-1',
    accountName: '',
    duration: '7 days'
  });
  const updateFilters = (newFilters) => {
    setFilterState(prev => ({
      ...prev,
      ...newFilters
    }));
  };

  const [authError, setAuthError] = useState(false);
  const [lastUpdatedTime, setLastUpdatedTime] = useState(null);
  const [loadingStates, setLoadingStates] = useState({});
  const [lastFetchParams, setLastFetchParams] = useState(null);
  const [dataCache, setDataCache] = useState(new Map());
  const cacheLimit = 3;
  const [cacheTimeLimit, setCacheTimeLimit] = useState(100000);
  const location = useLocation();
  
  const accessToken = useSelector((state) => state.accessToken.value);
  const apiUrl = process.env.REACT_APP_API_URL;

  const createCacheKey = useCallback((params) => {
    return `${params.duration}-${params.accountName}-${params.region}`;
  }, []);



  const getFromCache = useCallback((params) => {
    const key = createCacheKey(params);
    const cachedData = dataCache.get(key);
    if (cachedData) {
      if (Date.now() - cachedData.timestamp > cacheTimeLimit) {
        dataCache.delete(key);
        return undefined;
      }
      setSecurityData(cachedData.data);
      setLastFetchParams(params);
      setLastUpdatedTime(new Date(cachedData.timestamp).toLocaleTimeString());
    }
    return cachedData;
  }, [dataCache, cacheTimeLimit]);

  const updateCache = useCallback((params, data) => {
    const key = createCacheKey(params);
       setDataCache(prev => {
           const newCache = new Map(prev);
         newCache.set(key, {
           data: data,
           timestamp: Date.now() 
         });
           if (newCache.size > cacheLimit) {
             const firstKey = newCache.keys().next().value;
             newCache.delete(firstKey);
           }
           return newCache;
         });
  }, []);

  const fetchSecurityData = async (params) => {
    const { duration, accountName, region } = params;
    const paramsKey = createCacheKey(params);

    const cachedData = getFromCache(params);
    if (cachedData) {
      setSecurityData(cachedData);
      setLastFetchParams(params);
      return;
    }

    if (loadingStates[paramsKey]) return;
    
    setLoadingStates(prev => ({ ...prev, [paramsKey]: true }));

    try {
        const queries = {
          s3: `query MyQuery($duration: String!, $accountName: String!, $region: String!) {
            getS3bucketData(
              duration: $duration
              filters: {region: $region, accountName: $accountName}
            ) {
              data {
                acl_settings
                awsRegion
                bucket_name
                bucket_region
                errorCode
                errorMessage
                eventname
                username
                user_type
                sourceipaddress
                userAgent
                role_name
                requestid
                recipientAccountId
                public_access_config
                eventtype
                eventtime
              }
              summary {
                createBucketCount
                deleteBucketCount
                deleteBucketPolicyCount
                deletePublicAccessBlockCount
                errorCount
                putBucketAclCount
                putBucketEncryptionCount
                putBucketLoggingCount
                putBucketVersioningCount
                putPublicAccessBlockCount
                putBucketPolicyCount
              }
            }
          }`,
          login:`query MyQuery($duration:String!, $accountName:String!,$region: String!) {
   getLoginDetails(duration: $duration, filters: {accountName: $accountName, region:$region}) {
       summary {
      api_login_count
      average_ips_per_user
      console_login_count
      failed_login_count
      failed_login_percentage
      latest_login_time
      mfa_used_count
      most_active_user_count
      most_active_user
      mfa_used_percentage
      most_frequent_ip
      most_frequent_ip_count
      root_login_count
      total_logins
      unique_users
      unique_ip_count
      users_with_multiple_ips
    }
    total_logins
    data {
      AuthType
      Status
      MFAUsed
      account_id
      awsregion
      eventtime
      login_count
      ip_address
      userAgent
      username
    }
    }
  }`
          ,
          lb: `query MyQuery($duration: String!, $accountName: String!, $region: String!) {
            getLoadBalancerData(
              duration: $duration
              filters: {accountName: $accountName, region: $region}
            ) {
              summary {
                mostActiveUser
                mostActiveRegion
                configurationChanges {
                  attributeUpdates
                  securityGroupUpdates
                  subnetUpdates
                }
                errorCount
                listenerOperations {
                  createCount
                  deleteCount
                  modifyCount
                }
                loadBalancerOperations {
                  createCount
                  deleteCount
                  modifyCount
                }
                mostActiveRegionCount
                mostActiveUserCount
                targetGroupOperations {
                  createCount
                  modifyCount
                  deleteCount
                }
                uniqueRegionCount
                uniqueUserCount
              }
              data {
                awsregion
                errorcode
                errormessage
                eventname
                eventtime
                eventtype
                role_name
                sourceipaddress
                username
              }
            }
          }`,
          ec2: `query MyQuery($duration: String!, $accountName: String!, $region: String!) {
            getec2Data(
              duration: $duration
              filters: {accountName: $accountName, region: $region}
            ) {
              data {
                awsregion
                errorcode
                errormessage
                affected_resource
                role_name
                eventname
                eventtype
                eventtime
                sourceipaddress
                username
              }
              summary {
                configurationCount
                errorCount
                imageOperations {
                  createCount
                  deleteCount
                }
                instanceOperations {
                  rebootCount
                  runCount
                  startCount
                  stopCount
                  terminateCount
                }
                securityGroupCount
                snapshotOperations {
                  createCount
                  deleteCount
                }
                volumeOperations {
                  attachCount
                  createCount
                  deleteCount
                  detachCount
                }
              }
            }
          }`,
          rds: `query MyQuery($duration: String!, $accountName: String!, $region: String!) {
            getRdsInstanceData(
              duration: $duration
              filters: {accountName: $accountName, region: $region}
            ) {
              data {
                action_count
                db_engine
                db_instance
                eventname
                instance_class
                instance_status
                sourceipaddress
                username
                errorcode
                errormessage
              }
              summary {
                createCount
                deleteCount
                modifyCount
                startCount
                stopCount
              }
            }
          }`,
          ecs: `query MyQuery($duration: String!, $accountName: String!, $region: String!) {
            getEcsInstanceData(
              duration: $duration
              filters: {accountName: $accountName, region: $region}
            ) {
                data {
      cluster_name
      eventname
      username
      task_definition
      sourceipaddress
      service_name
      requestparameters
      eventtime
    }
    summary {
      clusterOperations {
        createCount
        deleteCount
        updateCount
      }
      serviceOperations {
        createCount
        deleteCount
        updateCount
      }
      taskOperations {
        deregisterCount
        registerCount
        runCount
        startCount
        stopCount
      }
    }
            }
          }`,
          kms: `query MyQuery($duration: String!, $accountName: String!, $region: String!) {
            getKmsInstanceData(
              duration: $duration
              filters: {accountName: $accountName, region: $region}
            ) {
              data {
                action_count
                eventname
                key_id
                sourceipaddress
                key_usage
                key_state
              }
              summary {
                createKeyCount
                disableKeyCount
                enableKeyCount
                scheduleKeyDeletionCount
              }
            }
          }`,
          waf : `query MyQuery($duration: String!, $accountName: String!, $region: String!) {
            getWafInstanceData(
              duration: $duration
              filters: {accountName: $accountName, region: $region}
            ) {
               data {
      eventname
      eventtime
      eventtype
      recipientaccountid
      sourceipaddress
      useragent
      username
      waf_default_action
      waf_id
      waf_name
      waf_rule_count
      waf_rule_names
      waf_scope
      api_key
    }
              summary {
      ruleGroupOperations {
        createCount
        deleteCount
        updateCount
      }
      ruleOperations {
        createCount
        deleteCount
        updateCount
      }
      webACLOperations {
        createCount
        deleteCount
        updateCount
      }
      apiKeyOperations {
        createCount
        deleteCount
        updateCount
      }
    }
            }
          }`
        };
  
        const [s3Response, lbResponse, ec2Response, rdsResponse, ecsResponse, kmsResponse,loginResponse,wafResponse] = await Promise.all([
          axios.post(apiUrl, { query: queries.s3, variables: params }, { headers: { Authorization: accessToken } }),
          axios.post(apiUrl, { query: queries.lb, variables: params }, { headers: { Authorization: accessToken } }),
          axios.post(apiUrl, { query: queries.ec2, variables: params }, { headers: { Authorization: accessToken } }),
          axios.post(apiUrl, { query: queries.rds, variables: params }, { headers: { Authorization: accessToken } }),
          axios.post(apiUrl, { query: queries.ecs, variables: params }, { headers: { Authorization: accessToken } }),
          axios.post(apiUrl, { query: queries.kms, variables: params }, { headers: { Authorization: accessToken } }),
          axios.post(apiUrl, { query: queries.login, variables: params }, { headers: { Authorization: accessToken } }),
          axios.post(apiUrl, { query: queries.waf, variables: params }, { headers: { Authorization: accessToken } })


        ]);
        const newData = {
          s3Data: s3Response.data?.data?.getS3bucketData || null,
          lbData: lbResponse.data?.data?.getLoadBalancerData || null,
          ec2Data: ec2Response.data?.data?.getec2Data || null,
          rdsData: rdsResponse.data?.data?.getRdsInstanceData || null,
          ecsData: ecsResponse.data?.data?.getEcsInstanceData || null,
          kmsData: kmsResponse.data?.data?.getKmsInstanceData || null,
          loginData: loginResponse.data?.data?.getLoginDetails || null,
          wafData: wafResponse.data?.data?.getWafInstanceData || null
        };
    if (
      s3Response.data.errors &&
      s3Response.data.errors[0].errorType == "Unauthorized"
    ) {
      setAuthError("Unauthorized");
    }
      setSecurityData(prev => ({
        ...prev,
        ...newData
      }));
      updateCache(params, { ...securityData, ...newData });
      setLastUpdatedTime(new Date().toLocaleTimeString());
      setLastFetchParams(params);
    } catch (error) {
      console.error('Error fetching security data:', error);
    } finally {
      setLoadingStates(prev => ({ ...prev, [paramsKey]: false }));
    }
  };

  const shouldRefetch = useCallback((newParams) => {
    if (!lastFetchParams) return true;
    if (getFromCache(newParams)) return false;
    
    return (
      newParams.duration !== lastFetchParams.duration ||
      newParams.accountName !== lastFetchParams.accountName ||
      newParams.region !== lastFetchParams.region
    );
  }, [lastFetchParams, getFromCache]);

  const clearCache = useCallback(() => {
    setDataCache(new Map());
  }, []);

  return (
    <SecurityDataContext.Provider value={{
      securityData,
      fetchSecurityData,
      shouldRefetch,
      clearCache,
         filterState,
    updateFilters,
      isLoading: Object.values(loadingStates).some(Boolean),
      lastUpdatedTime,
      authError
    }}>
      {children}
    </SecurityDataContext.Provider>
  );
};

export const useSecurityData = () => {
  const context = useContext(SecurityDataContext);
  if (!context) {
    throw new Error('useSecurityData must be used within a SecurityDataProvider');
  }
  return context;
};

export default SecurityDataProvider;